import { PDF_STACK_SIZE } from "./homeSample";

/**
 * The "before" of the landing comparison: a stack of pages from a dull
 * vendor account-review PDF, built from the same data as the Loopwire /
 * Northfield sample narrative. The top page leads with the quarterly
 * hours-saved table, which is the narrative page the "after" side shows
 * (see COMPARISON_SCREENSHOT), so the two read as the same content.
 *
 * Runs per quarter are derived from the narrative's 12,840 total in
 * proportion to hours saved; everything else is the narrative's own data.
 */

const QUARTER_ROWS: [string, string, string, string, string][] = [
  ["Q1 2025", "2,694", "81", "21.0%", "—"],
  ["Q2 2025", "3,293", "99", "25.6%", "+22.2%"],
  ["Q3 2025 ¹", "3,826", "115", "29.8%", "+16.2%"],
  ["Q4 2025", "3,027", "91", "23.6%", "−20.9%"],
];
const QUARTER_BARS = [81, 99, 115, 91];

const PAGE =
  "absolute w-[500px] h-[650px] bg-white border border-[#dcdad5] shadow-[0_6px_24px_rgba(17,17,24,0.12)] font-serif text-[#55535a] px-9 py-7";

const RunningHeader = () => (
  <div className="flex justify-between text-[9px] tracking-wide uppercase text-[#8a8890] border-b border-[#d8d6d2] pb-1.5 mb-4">
    <span>Loopwire &middot; Account Review</span>
    <span>Northfield Marketing &middot; FY2025</span>
  </div>
);

/** Grey placeholder text lines for the pages peeking out behind. */
const Lines = ({
  count,
  widths = [100, 96, 92, 98, 70],
}: {
  count: number;
  widths?: number[];
}) => (
  <div className="space-y-[7px]">
    {Array.from({ length: count }, (_, i) => (
      <div
        key={i}
        className="h-[5px] bg-[#e4e2de] rounded-sm"
        style={{ width: `${widths[i % widths.length]}%` }}
      />
    ))}
  </div>
);

export const SamplePdfStack = () => (
  <div
    role="img"
    aria-label="A stack of pages from an annual account-review PDF for Northfield Marketing: small print, a table of automation hours saved by quarter, and a grey bar chart."
    className="group relative select-none"
    style={PDF_STACK_SIZE}
  >
    <div aria-hidden="true">
      {/* Back page: peer benchmark */}
      <div
        className={`${PAGE} left-[70px] top-[30px] rotate-[4deg] transition-transform duration-500 group-hover:translate-x-6 group-hover:rotate-[6deg]`}
      >
        <RunningHeader />
        <div className="text-[13px] font-bold text-[#3d3b42] mb-3">
          4. Peer Benchmark
        </div>
        <Lines count={14} />
        <div className="text-[9px] text-right text-[#a3a1a8] absolute bottom-6 right-9">
          Page 14 of 24
        </div>
      </div>

      {/* Middle page: executive summary */}
      <div
        className={`${PAGE} left-[40px] top-[18px] -rotate-[3deg] transition-transform duration-500 group-hover:-translate-x-5 group-hover:-rotate-[5deg]`}
      >
        <RunningHeader />
        <div className="text-[13px] font-bold text-[#3d3b42] mb-3">
          2. Executive Summary
        </div>
        <Lines count={16} />
      </div>

      {/* Top page: the quarterly hours-saved table */}
      <div
        className={`${PAGE} left-[50px] top-[48px] transition-transform duration-500 group-hover:-translate-y-2`}
      >
        <RunningHeader />
        <div className="text-[13px] font-bold text-[#3d3b42] mb-2">
          3.2 Automation Hours Saved by Quarter
        </div>
        <p className="text-[9px] leading-[1.5] mb-3 text-justify">
          Hours saved are estimated from completed automation runs using the
          task-time baselines agreed at onboarding (Appendix B). Figures cover
          all 8 licensed users and 47 active workflows for the period January 1
          to December 31, 2025, and exclude runs that failed or required manual
          completion (6.0% of total). Share of annual is computed against the
          full-year total.
        </p>
        <table className="w-full text-[9px] border-collapse mb-3 tabular-nums">
          <thead>
            <tr className="border-y border-[#bdbbb6] text-left">
              <th className="py-1 font-bold">Period</th>
              <th className="py-1 font-bold text-right">Automation runs</th>
              <th className="py-1 font-bold text-right">Hours saved</th>
              <th className="py-1 font-bold text-right">Share of annual</th>
              <th className="py-1 font-bold text-right">QoQ &Delta;</th>
            </tr>
          </thead>
          <tbody>
            {QUARTER_ROWS.map((row) => (
              <tr key={row[0]} className="border-b border-[#e6e4e0]">
                {row.map((cell, i) => (
                  <td
                    key={i}
                    className={`py-[3px] ${i === 0 ? "" : "text-right"}`}
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
            <tr className="font-bold border-t border-[#bdbbb6]">
              <td className="py-[3px]">FY2025 total</td>
              <td className="py-[3px] text-right">12,840</td>
              <td className="py-[3px] text-right">386</td>
              <td className="py-[3px] text-right">100.0%</td>
              <td className="py-[3px] text-right" />
            </tr>
          </tbody>
        </table>
        <div className="flex items-end gap-5 h-[110px] border-l border-b border-[#bdbbb6] px-3 mx-1">
          {QUARTER_BARS.map((h, i) => (
            <div
              key={i}
              className="flex-1 bg-[#c9c7c3]"
              style={{ height: `${(h / 130) * 100}%` }}
            />
          ))}
        </div>
        <div className="flex justify-around text-[8px] text-[#8a8890] mt-1 mx-1">
          <span>Q1</span>
          <span>Q2</span>
          <span>Q3</span>
          <span>Q4</span>
        </div>
        <div className="text-[8.5px] italic text-center text-[#8a8890] mt-1.5">
          Figure 7. Estimated hours saved per quarter
        </div>
        <p className="text-[8px] leading-[1.45] mt-3 text-[#8a8890]">
          ¹ Q3 variance is attributable to the Westmark holiday push (47
          micro-workflows deployed over 11 days). Top workflow by volume: Weekly
          client report rollup (1,240 runs).
        </p>
        <div className="absolute bottom-6 left-9 right-9 flex justify-between text-[8px] text-[#a3a1a8] border-t border-[#e6e4e0] pt-1.5">
          <span>Loopwire, Inc. &mdash; Confidential</span>
          <span>Page 9 of 24</span>
        </div>
      </div>
    </div>
  </div>
);
